import { IServiceState } from 'app/reducers/service/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RunningState } from 'shared-types/bookingStatusTypes';
import { ILayout, IServiceItem, IServiceListView, SECTION_ANY_ID } from 'shared-types/SharedTypes';
import { DiaryRootState } from 'appRoot/rootReducer';
import BookingLookupService from 'external-services/booking-lookup-service';
import { BookingAvailabilityService } from 'app/services/LegacyBookingAvailabilityService';
import { IServiceViewItem } from 'external-services/booking-lookup-service/types';
import {
  IBookingInfoAtTime,
  ILayoutSubset, IScheduleSubset,
  IServiceItemSubset,
} from 'app/services/LegacyBookingAvailabilityService/types';
import { IBookingsByServiceObj } from 'external-services/booking-generator-service/types';
import FloorplanBookingFilterService from 'floorplan-v2/app/services/floorplanBookingFilter';

const NS = 'serviceSlice';
const initialState: IServiceState = {
  currentServiceViews: [],
  currentServiceState: null,
  selectedServiceView: null,
  currentServiceTimes: [],
  latestTimeOfTheDay: null,
  latestTimeOfTheDayBySection: {}
};

export const getServiceTimesFromSelectedService = createAsyncThunk(
  'service/getServiceTimesFromSelectedService',
  async (payload: {selectedServiceView: number, currentServiceViews: IServiceListView[]}, { dispatch, getState }): Promise<IBookingInfoAtTime[]> => {
    const { scheduleReducer, bookingReducer, layoutReducer } = getState() as DiaryRootState;
    const {selectedSchedule} = scheduleReducer;
    const {couchReducedBookings} = bookingReducer;
    const {couchReducedLayouts} = layoutReducer;

    const {selectedServiceView, currentServiceViews} = payload;

    let bookingTimesNew: IBookingInfoAtTime[] = [];
    const selectedService = currentServiceViews[selectedServiceView]?.service;
    const haveAtleastOneFunctionTrackerBooking = couchReducedBookings?.some(booking => booking.functionTracker === true) || false;
    const bookingByServiceFP = BookingLookupService.setBookingsByServiceObject(currentServiceViews as any as IServiceViewItem[], haveAtleastOneFunctionTrackerBooking);

    // for All Times - we add all the times from all the services and then remove duplicates
    if (!selectedService) {
      const availableServices = FloorplanBookingFilterService.getAvailableServices(currentServiceViews, currentServiceViews[selectedServiceView])

      const allTimes = getAllTimes(availableServices, selectedSchedule, couchReducedLayouts, bookingByServiceFP)

      bookingTimesNew = FloorplanBookingFilterService.removeDuplicateTimes(allTimes, 'time')
    }

    // only run for services not for All
    if (selectedService) {
      bookingTimesNew = BookingAvailabilityService.calulateBookingTimes(
        selectedSchedule, selectedService as IServiceItemSubset, couchReducedLayouts as unknown as ILayoutSubset[],
        (selectedService as IServiceItem).bookingDuration, 2, [], bookingByServiceFP,
        SECTION_ANY_ID, null, true,
        null, true,
      );
    }
    return bookingTimesNew
  }
);



function getAllTimes(availableServices: IServiceItem[], selectedSchedule: IScheduleSubset,
                     couchReducedLayouts: ILayout[], bookingByServiceFP: IBookingsByServiceObj): IBookingInfoAtTime[]{
  let allTimes: IBookingInfoAtTime[] = [];

  availableServices.forEach((service) => {
    // section groups do not have their service information in the service object - instead they have it in services object
    const serviceTimes = BookingAvailabilityService.calulateBookingTimes(
      selectedSchedule, service as IServiceItemSubset, couchReducedLayouts as unknown as ILayoutSubset[],
      service.bookingDuration, 2, [], bookingByServiceFP,
      SECTION_ANY_ID, null, true,
      null, true
    );
    allTimes = [...allTimes, ...serviceTimes];
  });
  return allTimes;
}

// function removeDuplicateTimes(allTimes: IBookingInfoAtTime[]): IBookingInfoAtTime[] {
//   return allTimes.reduce((unique, o) => {
//     if (!unique.some(obj => obj.time.getTime() === o.time.getTime())) {
//       unique.push(o);
//     }
//     return unique;
//   }, []);
// }


const serviceSlice = createSlice({
  name: 'service',
  initialState,
  reducers: {
    setCurrentServiceViews: (state: IServiceState, action: PayloadAction<IServiceListView[]>) => {
      state.currentServiceViews = action.payload;
    },
    setLatestTimeOfTheDayBySection: (state: IServiceState, action: PayloadAction<IServiceListView[]>) => {
      state.latestTimeOfTheDayBySection = getLatestTimeOfTheDayBySection(action.payload)
    },
    setServiceState: (state: IServiceState, action: PayloadAction<RunningState>) => {
      state.currentServiceState = action.payload;
    },
    setSelectedServiceView: (state: IServiceState, action: PayloadAction<IServiceListView>) => {
      state.selectedServiceView = action.payload;
    },
    setLatestTimeOfTheDay: (state: IServiceState, action: PayloadAction<Date>) => {
      state.latestTimeOfTheDay = action.payload;
    },
    reset: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getServiceTimesFromSelectedService.fulfilled, (state: IServiceState, action) => {
      state.currentServiceTimes = action.payload || [];
    });
  },
});

export const getLatestTimeOfTheDayBySection = (currentServiceViews: IServiceListView[]) => {
  return currentServiceViews.reduce((latestTimes, { service }) => {
    if (!service) return latestTimes; // Skip if service is null | undefined | 0

    const serviceItem = service as IServiceItem
    serviceItem.sectionLayouts.forEach(
      ({ sectionId, sectionNotUsed, sectionClosed }) => {
        const isSectionClosed = sectionNotUsed || sectionClosed;
        const timeToSet = isSectionClosed
          ? serviceItem.startTime
          : serviceItem.endTime;

        if (!latestTimes[sectionId]) {
          latestTimes[sectionId] = timeToSet as Date;
        } else {
          if (!isSectionClosed) {
            latestTimes[sectionId] = serviceItem.endTime as Date;
          }
        }
      }
    );

    return latestTimes;
  }, {} as Record<string, Date>);
};

// Extract the action creators object and the reducer
const { actions, reducer } = serviceSlice;
// Extract and export each action creator by name
export { actions as serviceActions };
// Export the reducer, either as a default or named export
export default reducer;
